import React, { useState, useEffect } from "react";

/**
 * Right-side visual like promo.com:
 * - Circular preview with spinning arc
 * - Grid of mini posts inside the circle
 * - "Generating your video posts..." caption
 */
export default function PromoProgressPanel({ dark = false, caption = "Generating your video posts..." }) {
  const [thumbs, setThumbs] = useState([]);
  const [loadedImages, setLoadedImages] = useState(new Array(8).fill(false)); // Updated to an array of 8 images

  const tileBg = dark ? "bg-gray-800" : "bg-white";
  const tileBorder = dark ? "border-gray-700" : "border-gray-200";
  const ringTrack = dark ? "#374151" /* gray-700 */ : "#E5E7EB" /* gray-200 */;
  const ringStroke = dark ? "#F43F5E" /* rose-500 */ : "#EC4899" /* pink-500 */;

  const fallbackImage = "/mnt/data/9695862c-fabd-4d14-96a7-1c12ec9c38a7.png"; // The fallback image you provided

  // Initialize thumbs (image array) inside useEffect to prevent errors
  useEffect(() => {
    const newThumbs = [
  "https://source.unsplash.com/1600x900/?artificialintelligence,technology",
  "https://source.unsplash.com/1600x900/?machinelearning,AI",
  "https://source.unsplash.com/1600x900/?contentcreation,AI",
  "https://source.unsplash.com/1600x900/?data,AI",
  "https://source.unsplash.com/1600x900/?automation,AI",
  "https://source.unsplash.com/1600x900/?technology,processing",
  "https://source.unsplash.com/1600x900/?machinelearning,processing",
  "https://source.unsplash.com/1600x900/?ai,creativity",
];




    setThumbs(newThumbs);
  }, []);

  const handleError = (index) => {
    // Update the state when an image fails to load
    setLoadedImages((prev) => {
      const newLoadedImages = [...prev];
      newLoadedImages[index] = true; // Mark as failed
      return newLoadedImages;
    });
  };

  return (
    <div className={`relative flex flex-col items-center justify-center w-full h-full p-8`}>
      <div className="relative">
        {/* outer ring track */}
        <svg width="420" height="420" viewBox="0 0 420 420" className="block">
          <circle cx="210" cy="210" r="190" fill="none" stroke={ringTrack} strokeWidth="12" />
        </svg>

        {/* spinning arc */}
        <svg
          width="420"
          height="420"
          viewBox="0 0 420 420"
          className="absolute inset-0 animate-dash-rotate"
          style={{ animationDuration: "3.5s" }}
        >
          <circle
            cx="210"
            cy="210"
            r="190"
            fill="none"
            stroke={ringStroke}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray="1200"
            strokeDashoffset="800"
          />
        </svg>

        {/* inner circle that clips a grid of mini posts */}
        <div
          className="absolute inset-0 grid place-items-center"
          style={{ pointerEvents: "none" }}
        >
          <div
            className={`rounded-full overflow-hidden ${tileBorder} border`}
            style={{ width: 330, height: 330 }}
          >
            <div className="grid grid-cols-3 gap-1 w-full h-full">
              {thumbs.map((src, i) => (
                <div
                  key={i}
                  className={`relative ${tileBg} ${tileBorder} border overflow-hidden animate-float`}
                  style={{ animationDelay: `${(i % 6) * 0.15}s` }}
                >
                  <img
                    src={loadedImages[i] ? fallbackImage : src} // Use fallback image if failed
                    alt="Thumbnail"
                    className="w-full h-full object-cover"
                    onError={() => handleError(i)} // Add error handler for each image
                  />
                  {/* tiny UI chrome like a post */}
                  <div className="absolute bottom-0 left-0 right-0 h-5 bg-black/40" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* small tick that rides the ring (gives extra motion) */}
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-24 h-24 rounded-full border-2 border-pink-500/60 blur-sm animate-spin-slower" />
      </div>

      <div className="mt-6 text-sm md:text-base font-medium text-gray-700 dark:text-gray-300">
        {caption}
      </div>
    </div>
  );
}
